import { Component } from '@angular/core';
import { PLUGINS, THEMES, TOOLS } from 'src/app/data/product/product.data';
import { buildAppearanceSections } from '../../../data/theme/appearance-menu';
import { DarkLightSettings } from '../../../data/theme/theme.data';
import { URLS } from '../../../data/navigation/navigation.data';
import { Product, ProductLine } from '../../../model/product/product.model';
import { AnalyticsService } from '../../../service/analytics/analytics.service';
import { CartService } from '../../../service/cart/cart.service';
import { LoadingService } from '../../../service/loading/loading.service';
import { RoutingService } from '../../../service/routing/routing.service';
import { ThemeService } from '../../../service/theme/theme.service';
import { DropdownCoordinatorService } from '../../dropdown-menu/dropdown-coordinator.service';
import { DropdownMenuSection } from '../../dropdown-menu/dropdown-menu.component';

@Component( {
  selector: 'app-bottom-nav',
  templateUrl: './bottom-nav.component.html',
  styleUrls: [ './bottom-nav.component.scss' ]
} )
export class BottomNavComponent {
  loading = false;

  // Paged nav items: at most 5 buttons per view. Page 0 = 4 items + More;
  // middle pages = Back + 3 items + More; the last page = Back + the rest.
  // Overflowing items always flow into the next page.
  readonly navItems: { label: string, icon: string, link?: string, action?: 'appearance', badge?: 'cart' }[] = [
    // the top nav's order (owner 2026-10-11: Apps & Plugins, Themes, Tools, Palettes, Blog; Customers removed 2026-10-12), with
    // Home first and the Cart kept on the first page
    { label: 'Home', icon: 'home', link: URLS.root },
    { label: 'Apps & Plugins', icon: 'puzzle-piece', link: URLS.plugins.plugins },
    { label: 'Themes', icon: 'brush', link: URLS.themes },
    { label: 'Cart', icon: 'cart-shopping', link: URLS.cart, badge: 'cart' },
    { label: 'Tools', icon: 'toolbox', link: URLS.tools.tools },
    { label: 'Palettes', icon: 'palette', link: URLS.palettes },
    { label: 'Blog', icon: 'file-lines', link: URLS.blog },
    { label: 'Appearance', icon: 'circle-half-stroke', action: 'appearance' },
    { label: 'Contact Us', icon: 'envelope', link: URLS.contactUs },
  ];

  page = 0;

  URLS = URLS;

  PLUGINS: ProductLine[] = PLUGINS;
  THEMES: ProductLine[] = THEMES;
  TOOLS: ProductLine[] = TOOLS;

  currentTheme: string = DarkLightSettings.auto;
  currentProductKey: string | null = null;
  /** copies in the store cart (the badge on the Cart item) */
  cartCount = 0;

  constructor(
    private loadingService: LoadingService,
    private routingService: RoutingService,
    private themeService: ThemeService,
    private coordinator: DropdownCoordinatorService,
    private analyticsService: AnalyticsService,
    private cartService: CartService,
  ) {
    this.loadingService.sharedLoading.subscribe( loading => this.loading = loading );
    this.cartService.count$.subscribe( count => this.cartCount = count );
    this.themeService.darkLightSetting.subscribe(
      value => this.currentTheme = value || DarkLightSettings.auto
    );
    this.themeService.productTheme.subscribe(
      productTheme => this.currentProductKey = productTheme ? productTheme.key : null
    );
  }

  private cachedSections: DropdownMenuSection[] = [];
  private sectionsCacheKey = '';

  // Memoized so the getter returns a STABLE array reference across change
  // detection (rebuilding only when the mode or selected theme actually
  // changes). Without this every CD cycle handed the dropdown a fresh array,
  // churning the DOM and breaking hover.
  get settingsSections(): DropdownMenuSection[] {
    const key = this.currentTheme + '|' + this.currentProductKey;
    if ( key !== this.sectionsCacheKey ) {
      this.sectionsCacheKey = key;
      this.cachedSections = buildAppearanceSections( this.currentTheme, this.currentProductKey );
    }
    return this.cachedSections;
  }

  onSettingSelect( value: string ) {
    // Mirror the top-nav appearance tracking from the mobile bottom nav so the
    // "Appearance Changed" metric (with the readable `selection` to break down
    // by) covers both entry points.
    const kind = this.appearanceKind( value );
    this.analyticsService.logEvent( 'Appearance Changed', {
      selection: this.appearanceLabel( value, kind ),
      kind,
      source: 'bottom-nav',
    } );
    if ( value === DarkLightSettings.light || value === DarkLightSettings.auto || value === DarkLightSettings.dark ) {
      // The modes are the Default row's choices, so picking one while a
      // product theme is active drops back to the default palette in that
      // mode, no forcing a light theme into dark (e.g. a "Dark Autumn").
      if ( this.currentProductKey ) {
        this.themeService.resetThemeColor();
      }
      this.themeService.setDarkLightSetting( value );
      return;
    }
    if ( value === 'default' ) {
      this.themeService.resetThemeColor();
      return;
    }
    const product = this.findThemeProduct( value );
    if ( product ) {
      this.themeService.setProductTheme( product );
    }
  }

  /** Classify an Appearance menu value for analytics: a dark/light/auto mode
   *  toggle, a reset to the default palette, or a product-theme pick. */
  private appearanceKind( value: string ): string {
    if ( value === DarkLightSettings.light || value === DarkLightSettings.auto || value === DarkLightSettings.dark ) {
      return 'mode';
    }
    return value === 'default' ? 'reset' : 'theme';
  }

  /** Human-readable label for the appearance choice, used as the Mixpanel
   *  breakdown property: 'Light' / 'Dark' / 'Auto' for modes, 'Default' for a
   *  reset, and the theme's display name (e.g. 'Aurora Borealis') for a pick. */
  private appearanceLabel( value: string, kind: string ): string {
    if ( kind === 'mode' ) {
      return value.charAt( 0 ).toUpperCase() + value.slice( 1 );
    }
    if ( kind === 'reset' ) {
      return 'Default';
    }
    // Always report the theme name in Title Case (e.g. 'Aurora Borealis',
    // 'Rio De Janeiro'), capitalising every word, falls back to the slug.
    const name = this.findThemeProduct( value )?.name || value;
    return name.replace( /[-_]+/g, ' ' ).replace( /\b\w/g, ( c ) => c.toUpperCase() );
  }

  private findThemeProduct( key: string ): Product | undefined {
    for ( const line of this.THEMES ) {
      const found = line.products.find( p => p.key === key );
      if ( found ) {
        return found;
      }
    }
    return undefined;
  }

  /** Items visible on the current page (max 5 buttons incl. Back/More). */
  get pageItems(): { label: string, icon: string, link?: string, action?: string }[] {
    const first = 4;              // page 0: 4 items + More
    const middle = 3;             // later pages: Back + 3 items + More
    if ( this.page === 0 ) {
      return this.navItems.slice( 0, first );
    }
    const start = first + ( this.page - 1 ) * middle;
    // The last page trades its More slot for one extra item (Back + 4).
    const remaining = this.navItems.length - start;
    return this.navItems.slice( start, start + ( remaining <= 4 ? 4 : middle ) );
  }

  get hasNextPage(): boolean {
    const first = 4;
    const middle = 3;
    const start = this.page === 0 ? 0 : first + ( this.page - 1 ) * middle;
    const remaining = this.navItems.length - start;
    // page 0 overflows past 4 items; middle pages overflow past Back+4.
    return this.page === 0 ? remaining > first : remaining > 4;
  }

  /** Invisible filler slots on the last page (after Back, on the left) so
   *  every page lays out exactly 5 equal-width buttons. */
  get fillerSlots(): number[] {
    if ( this.page === 0 || this.hasNextPage ) {
      return [];
    }
    const used = 1 + this.pageItems.length; // Back + items
    return Array.from( { length: Math.max( 0, 5 - used ) } );
  }

  nextPage(): void {
    this.page++;
  }

  prevPage(): void {
    this.page = Math.max( 0, this.page - 1 );
  }

  redirect( url: string ) {
    this.coordinator.closeOthers();
    this.routingService.redirect( url );
  }
}
